import React, { useEffect, useRef, useState } from "react";

const SEARCH_LS_PREFIX = "freight_admin_search";

function readSavedSearch(pathname) {
  if (!pathname) return "";
  try {
    const raw = localStorage.getItem(`${SEARCH_LS_PREFIX}:${pathname}`);
    return raw == null ? "" : String(raw);
  } catch {
    return "";
  }
}

function writeSavedSearch(pathname, value) {
  if (!pathname) return;
  try {
    localStorage.setItem(`${SEARCH_LS_PREFIX}:${pathname}`, String(value ?? ""));
  } catch {
    /* ignore */
  }
}

/**
 * Top controls in the same pattern as seller My Offers (`OfferFilters`):
 * `card filter-card` + `filter-row` — search, Columns, optional primary CTA.
 */
const FreightAdminTableToolbar = ({
  searchValue,
  onSearchChange,
  searchPlaceholder = "Search…",
  columnDefs,
  visibility,
  onToggleColumn,
  onResetColumns,
  onResetColumnOrder,
  rightSlot,
  hideSearch = false
}) => {
  const [colsOpen, setColsOpen] = useState(false);
  const colsRef = useRef(null);
  const hydratedRef = useRef(false);

  useEffect(() => {
    if (hideSearch) return;
    if (typeof window === "undefined") return;
    if (typeof onSearchChange !== "function") return;
    if (hydratedRef.current) return;
    hydratedRef.current = true;
    const saved = readSavedSearch(window.location.pathname);
    if (saved && String(searchValue ?? "") !== saved) {
      onSearchChange(saved);
    }
  }, [hideSearch, onSearchChange, searchValue]);

  useEffect(() => {
    const close = (e) => {
      if (colsRef.current && !colsRef.current.contains(e.target)) setColsOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  return (
    <div className="card filter-card mb-3">
      <div className="filter-row">
        {!hideSearch && (
          <input
            type="search"
            value={searchValue}
            onChange={(e) => {
              const v = e.target.value;
              onSearchChange(v);
              if (typeof window !== "undefined") {
                writeSavedSearch(window.location.pathname, v);
              }
            }}
            placeholder={searchPlaceholder}
            aria-label="Search table"
          />
        )}

        <div className="flex flex-wrap items-center gap-2">
          <div className="relative" ref={colsRef}>
            <button
              type="button"
              className="btn-outline"
              onClick={() => setColsOpen((o) => !o)}
            >
              Columns
            </button>
            {colsOpen && (
              <div className="absolute right-0 mt-1 z-30 w-56 max-h-72 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg py-2">
                <button
                  type="button"
                  className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50"
                  style={{ color: "var(--base-color, #064e8d)" }}
                  onClick={() => {
                    onResetColumns?.();
                    setColsOpen(false);
                  }}
                >
                  Show all columns
                </button>
                {onResetColumnOrder && (
                  <button
                    type="button"
                    className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 text-gray-700"
                    onClick={() => {
                      onResetColumnOrder();
                      setColsOpen(false);
                    }}
                  >
                    Reset column order
                  </button>
                )}
                <div className="border-t border-gray-100 my-1" />
                {columnDefs.map((d) => (
                  <label
                    key={d.key}
                    className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={visibility[d.key] !== false}
                      onChange={() => onToggleColumn(d.key)}
                    />
                    {d.label}
                  </label>
                ))}
                {onResetColumnOrder ? (
                  <p className="px-3 pt-2 pb-1 text-[11px] text-gray-500 leading-snug m-0 border-t border-gray-100">
                    Use the grip (<i className="fas fa-grip-vertical text-[10px]" aria-hidden />) on a column header to
                    reorder; saved in this browser.
                  </p>
                ) : null}
              </div>
            )}
          </div>
          {rightSlot}
        </div>
      </div>
    </div>
  );
};

export default FreightAdminTableToolbar;
